/**
 * Redux slice for managing error modals (quota, concurrent limits, etc.)
 * Centralized state management for upgrade prompts and error notifications
 */

import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

/**
 * Structured quota error codes returned by the backend (quota_v2).
 * These map to context-specific overflow prompts in OverflowPrompt.tsx.
 */
export type QuotaErrorCode =
  | 'DAILY_LIMIT_REACHED'
  | 'MONTHLY_CAP_REACHED'
  | 'TRIAL_EXPIRED'
  | 'MODE_NOT_AVAILABLE'
  | 'INSUFFICIENT_CREDITS'
  /**
   * Per-tier monthly document-generation cap (issue #7468). 403 from the
   * /conversation dispatch when flow_type='document_generation'. Resolved by
   * upgrading (monthly plan restriction) — never by waiting out a daily reset,
   * and doc-gen has no PAYG path.
   */
  | 'DOC_GEN_MONTHLY_LIMIT_REACHED'
  /**
   * The active org finished onboarding and its Stripe subscription has since
   * ended (backend `quota_types.py`, issue #2544). Distinct from
   * `ORG_NOT_ACTIVE` below — the remedy here is to renew or switch to the
   * personal account, never "complete onboarding". Added by #11147; the
   * frontend never branched on it until #11315.
   */
  | 'ORG_SUSPENDED'
  /**
   * The active org has not finished onboarding (setup / dpa_pending /
   * dpa_signed) or does not exist (backend `quota_types.py`). Pre-existing
   * backend code; the frontend never branched on it until #11315.
   */
  | 'ORG_NOT_ACTIVE'
  /**
   * FE-only fallback: a bare 402 arrived without a recognizable structured
   * error_code or legacy inference signal. Never sent by the backend — only
   * assigned by inferQuotaErrorCode when none of the specific patterns match.
   * Renders the neutral "limit reached" scenario in OverflowPrompt.
   */
  | 'GENERIC_LIMIT';

export type ErrorSource = 'chat' | 'search' | 'export' | 'spisy';

export interface ErrorModalState {
  isOpen: boolean;
  errorCode: number | null; // 402 (quota exceeded), 409 (concurrent limit), etc.
  errorMessage: string | null;
  quotaErrorCode: QuotaErrorCode | null; // Structured error code from backend
  thinkingMode: string | null; // The thinking mode that triggered the limit (fast/thinking/deep)
  source: ErrorSource | null; // Which feature triggered the error
  isManualOpen: boolean; // true = user clicked upgrade button, false = actual error occurred
  /**
   * HTML id of the section to scroll to when the modal opens. Used when
   * a trigger context is specific to one pricing section (e.g. the
   * dashboard "Change plan" button jumps to ``professional-plans``
   * instead of the user-plan section). Null when no scroll is needed.
   */
  scrollAnchor: string | null;
  /**
   * Doc-gen monthly usage count / ceiling (DOC_GEN_MONTHLY_LIMIT_REACHED
   * only — issue #7468's `used`/`limit` fields). Locale-agnostic numbers so
   * OverflowPrompt can render a localized fact line instead of the backend's
   * raw English `message` string (issue #10336). Null for every other code.
   */
  docGenUsed: number | null;
  docGenLimit: number | null;
}

const initialState: ErrorModalState = {
  isOpen: false,
  errorCode: null,
  errorMessage: null,
  quotaErrorCode: null,
  thinkingMode: null,
  source: null,
  isManualOpen: false,
  scrollAnchor: null,
  docGenUsed: null,
  docGenLimit: null,
};

const errorModalSlice = createSlice({
  name: 'errorModal',
  initialState,
  reducers: {
    /**
     * Open the error modal with specific error details
     */
    openErrorModal: (
      state,
      action: PayloadAction<{
        errorCode: number;
        errorMessage?: string | null | undefined;
        quotaErrorCode?: QuotaErrorCode | null | undefined;
        thinkingMode?: string | null | undefined;
        source?: ErrorSource | undefined;
        isManualOpen?: boolean | undefined; // true = user clicked upgrade, false/undefined = actual error
        scrollAnchor?: string | undefined; // HTML id to scroll to after open (e.g. 'professional-plans')
        docGenUsed?: number | null | undefined; // DOC_GEN_MONTHLY_LIMIT_REACHED only
        docGenLimit?: number | null | undefined; // DOC_GEN_MONTHLY_LIMIT_REACHED only
      }>
    ) => {
      state.isOpen = true;
      state.errorCode = action.payload.errorCode;
      state.errorMessage = action.payload.errorMessage ?? null;
      state.quotaErrorCode = action.payload.quotaErrorCode ?? null;
      state.thinkingMode = action.payload.thinkingMode ?? null;
      state.source = action.payload.source ?? null;
      state.isManualOpen = action.payload.isManualOpen ?? false;
      state.scrollAnchor = action.payload.scrollAnchor ?? null;
      state.docGenUsed = action.payload.docGenUsed ?? null;
      state.docGenLimit = action.payload.docGenLimit ?? null;
    },

    /**
     * Close the error modal and reset state
     */
    closeErrorModal: (state) => {
      state.isOpen = false;
      state.errorCode = null;
      state.errorMessage = null;
      state.quotaErrorCode = null;
      state.thinkingMode = null;
      state.source = null;
      state.isManualOpen = false;
      state.scrollAnchor = null;
      state.docGenUsed = null;
      state.docGenLimit = null;
    },

    /**
     * Update error message without changing modal state
     */
    updateErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
  },
});

export const { openErrorModal, closeErrorModal } = errorModalSlice.actions;

export default errorModalSlice.reducer;
